/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
        Module No. 17, SBC, Thejaswini Building
        Phase 1, Technopark, Thiruvananthapuram - 695581, 
        Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { ALL_MLT_LAB_TEST_RECORDS, INITIAL_MLT_LAB_TEST_RECORDS, MLTLabTestRecord } from './mltLabTestData';
import HeaderSection from '../../common/headerSection';
import PageHeader from '../../common/pageHeader';
import Pagination from '../../../widgets/pagination';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';
import MLTLabTestRecording from './MLTLabTestRecording';

interface MLTLabTestProps {
    onNavigate?: () => void;
    onInitiateTest?: (record: MLTLabTestRecord) => void;
    onViewDetailedReport?: (record: MLTLabTestRecord) => void;
    onStartRecording?: (record: MLTLabTestRecord) => void;
}

export const MLTLabTest: React.FC<MLTLabTestProps> = ({
    onNavigate,
    onInitiateTest,
    onViewDetailedReport,
    onStartRecording
}) => {
    const { t } = useTranslation();

    // Master records in state so start/continue/complete updates reactively
    const [records, setRecords] = useState<MLTLabTestRecord[]>(ALL_MLT_LAB_TEST_RECORDS);

    // Active status tab: 'To Be Tested' | 'In Progress' | 'Completed' | 'All'
    const [activeTab, setActiveTab] = useState<'To Be Tested' | 'In Progress' | 'Completed'>('To Be Tested');

    // Filter States (matching screenshot)
    const [dateRange, setDateRange] = useState('01-Oct-2026 - 31-Oct-2026');
    const [searchSampleId, setSearchSampleId] = useState('');
    const [selectedSampleType, setSelectedSampleType] = useState('All');
    const [selectedTestParameter, setSelectedTestParameter] = useState('All');
    const [selectedStatus, setSelectedStatus] = useState('All');

    // Applied filter state (triggered by Search button or Tab click)
    const [appliedFilters, setAppliedFilters] = useState({
        dateRange: '01-Oct-2026 - 31-Oct-2026',
        searchSampleId: '',
        sampleType: 'All',
        testParameter: 'All',
        status: 'All'
    });

    const [useEffectRequired, setUseEffectRequired] = useState(false);
    // pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sort, setSort] = useState("id");
    const [order, setOrder] = useState("desc");
    const [fromdate, setFromDate] = useState(
        new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]
    );
    const currentDate = new Date().toISOString().split('T')[0];
    const [toDate, setToDate] = useState(currentDate);
    const [isInitiating, setIsInitiating] = useState(false);

    // Selected Record for Details Modal / Drawer
    const [selectedRecord, setSelectedRecord] = useState<MLTLabTestRecord | null>(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

    // In-progress / Start Test Workflow States inside Details Modal
    const [editingPlateCounts, setEditingPlateCounts] = useState<{ [plateNo: number]: string }>({});
    const [editingRemarks, setEditingRemarks] = useState('');
    const [selectedIncubator, setSelectedIncubator] = useState('Incubator INC-MB-02 (Chamber A)');
    const [toastMessage, setToastMessage] = useState<{ title: string; desc: string; type: 'success' | 'info' | 'warning' } | null>(null);

    // Print Report Modal State
    const [showPrintModal, setShowPrintModal] = useState(false);

    const showToast = (title: string, desc: string, type: 'success' | 'info' | 'warning' = 'info') => {
        setToastMessage({ title, desc, type });
        setTimeout(() => setToastMessage(null), 3500);
    };

    // Counts for each tab calculated from the current master records
    const tabCounts = useMemo(() => {
        const toBeTested = records.filter(r => r.status === 'Pending').length;
        const inProgress = records.filter(r => r.status === 'In Progress').length;
        const completed = records.filter(r => r.status === 'Completed').length;
        return { toBeTested, inProgress, completed };
    }, [records]);

    // Handle Search Trigger
    const handleSearch = () => {
        setAppliedFilters({
            dateRange,
            searchSampleId,
            sampleType: selectedSampleType,
            testParameter: selectedTestParameter,
            status: selectedStatus
        });
        // setCurrentPage(1);
        showToast(t('mltLabTest.toast.filtersApplied.title'), t('mltLabTest.toast.filtersApplied.desc'), 'info');
    };

    // Handle Clear Trigger
    const handleClear = () => {
        setDateRange('01-Oct-2026 - 31-Oct-2026');
        setSearchSampleId('');
        setSelectedSampleType('All');
        setSelectedTestParameter('All');
        setSelectedStatus('All');
        setAppliedFilters({
            dateRange: '',
            searchSampleId: '',
            sampleType: 'All',
            testParameter: 'All',
            status: 'All'
        });
        // setCurrentPage(1);
        showToast(t('mltLabTest.toast.filtersCleared.title'), t('mltLabTest.toast.filtersCleared.desc'), 'info');
    };

    // Filtered dataset
    const filteredRecords = useMemo(() => {
        return records.filter(rec => {
            // Tab filter
            if (activeTab === 'To Be Tested' && rec.status !== 'Pending') {
                // If Status dropdown specifically overrides, check that
                if (appliedFilters.status !== 'All' && appliedFilters.status !== 'Pending') return false;
                if (appliedFilters.status === 'All') return false;
            }
            if (activeTab === 'In Progress' && rec.status !== 'In Progress') {
                if (appliedFilters.status !== 'All' && appliedFilters.status !== 'In Progress') return false;
                if (appliedFilters.status === 'All') return false;
            }
            if (activeTab === 'Completed' && rec.status !== 'Completed') {
                if (appliedFilters.status !== 'All' && appliedFilters.status !== 'Completed') return false;
                if (appliedFilters.status === 'All') return false;
            }

            // Status dropdown filter
            if (appliedFilters.status !== 'All' && rec.status !== appliedFilters.status) {
                return false;
            }

            // Production Schedule ID & Sample ID search
            if (appliedFilters.searchSampleId.trim()) {
                const query = appliedFilters.searchSampleId.toLowerCase().trim();
                const matchesSchedule = rec.productionSchedule?.toLowerCase().includes(query);
                const matchesSampleId = rec.sampleId.toLowerCase().includes(query);
                const matchesTestId = rec.mltTestId.toLowerCase().includes(query);
                const matchesProduct = rec.productOrMaterial.toLowerCase().includes(query);
                if (!matchesSchedule && !matchesSampleId && !matchesTestId && !matchesProduct) {
                    return false;
                }
            }

            // Sample Type filter
            if (appliedFilters.sampleType !== 'All' && rec.sampleType !== appliedFilters.sampleType) {
                return false;
            }

            // Test Parameter filter
            if (appliedFilters.testParameter !== 'All' && rec.testParameter !== appliedFilters.testParameter) {
                return false;
            }

            return true;
        });
    }, [records, activeTab, appliedFilters]);

    // Pagination slice
    // const totalPages = Math.ceil(filteredRecords.length / pageSize) || 1;
    const paginatedRecords = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredRecords.slice(start, start + pageSize);
    }, [filteredRecords, page, pageSize]);

    // Open Details Modal for Record
    const handleOpenDetails = (rec: MLTLabTestRecord) => {
        setSelectedRecord(rec);
        setEditingRemarks(rec.remarks || '');
        // Initialize plate readings in editing state
        const countsMap: { [k: number]: string } = {};
        rec.plateReadings.forEach(p => {
            countsMap[p.plateNo] = p.cfu === '—' ? '' : String(p.cfu);
        });
        setEditingPlateCounts(countsMap);
        setIsDetailsModalOpen(true);
    };

    // Action: Start Test (Pending -> In Progress)
    const handleStartTest = (rec: MLTLabTestRecord) => {
        const now = new Date();
        const formattedDate = `${now.getDate() < 10 ? '0' : ''}${now.getDate()}-Oct-2026 ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

        // Calculate expected reading date (3 days later)
        const expected = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
        const expectedFormatted = `${expected.getDate() < 10 ? '0' : ''}${expected.getDate()}-Oct-2026 ${expected.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

        const updated = records.map(r => {
            if (r.id === rec.id) {
                return {
                    ...r,
                    status: 'In Progress' as const,
                    incubatorChamber: selectedIncubator,
                    actualIncubationTemp: rec.testParameter === 'TYMC' ? '22.3°C' : '32.4°C',
                    incubationStartTime: formattedDate,
                    expectedReadingDate: expectedFormatted,
                    remarks: `Incubation started in ${selectedIncubator}. Initial physical inspection passed.`
                };
            }
            return r;
        });

        setRecords(updated);
        const activeOne = updated.find(r => r.id === rec.id) || null;
        setSelectedRecord(activeOne);
        showToast(t('mltLabTest.toast.labTestStarted.title'), t('mltLabTest.toast.labTestStarted.desc', { testId: rec.mltTestId, incubator: selectedIncubator }), 'success');
    };

    // Action: Save Interim Observations (In Progress)
    const handleSaveInterimCounts = () => {
        if (!selectedRecord) return;
        const updated = records.map(r => {
            if (r.id === selectedRecord.id) {
                const updatedPlates = r.plateReadings.map(p => ({
                    ...p,
                    cfu: editingPlateCounts[p.plateNo] !== '' && !isNaN(Number(editingPlateCounts[p.plateNo]))
                        ? Number(editingPlateCounts[p.plateNo])
                        : p.cfu,
                    readingTimestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                }));

                return {
                    ...r,
                    plateReadings: updatedPlates,
                    remarks: editingRemarks
                };
            }
            return r;
        });

        setRecords(updated);
        setSelectedRecord(updated.find(r => r.id === selectedRecord.id) || null);
        showToast(t('mltLabTest.toast.interimCountsSaved.title'), t('mltLabTest.toast.interimCountsSaved.desc', { testId: selectedRecord.mltTestId }), 'success');
    };

    // Action: Complete Test (In Progress -> Completed)
    const handleCompleteTest = () => {
        if (!selectedRecord) return;
        const now = new Date();
        const formattedDate = `${now.getDate() < 10 ? '0' : ''}${now.getDate()}-Oct-2026 15:00`;

        // Calculate average and final CFU
        const validCounts: number[] = [];
        selectedRecord.plateReadings.forEach(p => {
            const val = editingPlateCounts[p.plateNo];
            if (val !== '' && !isNaN(Number(val))) {
                validCounts.push(Number(val));
            } else if (typeof p.cfu === 'number') {
                validCounts.push(p.cfu);
            }
        });

        const avg = validCounts.length > 0
            ? validCounts.reduce((a, b) => a + b, 0) / validCounts.length
            : 12.0;

        const calculatedBioburden = selectedRecord.sampleType === 'Water Sample'
            ? `< ${Math.max(10, Math.round(avg * 10))} CFU/100ml`
            : `${Math.round(avg * 10)} CFU/g`;

        const updated = records.map(r => {
            if (r.id === selectedRecord.id) {
                const updatedPlates = r.plateReadings.map((p, idx) => ({
                    ...p,
                    cfu: validCounts[idx] !== undefined ? validCounts[idx] : 12,
                    morphology: 'Verified colonies (uniform morphology)',
                    readingTimestamp: formattedDate
                }));

                return {
                    ...r,
                    status: 'Completed' as const,
                    completionDate: formattedDate,
                    plateReadings: updatedPlates,
                    averageCfuPerPlate: Number(avg.toFixed(1)),
                    calculatedBioburden,
                    complianceStatus: 'Pass' as const,
                    remarks: editingRemarks || 'Testing completed. All counts within validated pharmacopoeial acceptance limits.',
                    analystSignOff: {
                        name: 'Dr. Sarah Jenkins',
                        role: 'Senior QC Microbiologist',
                        signedAt: formattedDate
                    },
                    qcReviewSignOff: {
                        name: 'Emily Watson',
                        role: 'QC Laboratory Manager',
                        status: 'Approved' as const,
                        reviewedAt: `${now.getDate() < 10 ? '0' : ''}${now.getDate()}-Oct-2026 16:30`
                    }
                };
            }
            return r;
        });

        setRecords(updated);
        setSelectedRecord(updated.find(r => r.id === selectedRecord.id) || null);
        showToast(t('mltLabTest.toast.labTestCompleted.title'), t('mltLabTest.toast.labTestCompleted.desc', { testId: selectedRecord.mltTestId }), 'success');
    };

    //handle table pagination 
    const handlePageChange = (newPage: any, pageSize?: number) => {
        setPage(newPage);
        if (pageSize) {
            setPageSize(pageSize)
        }
        setUseEffectRequired(true)
    };

    return (
        <div>
            <HeaderSection />
            <PageHeader pageHeader={t('mltLabTest.header.title')} pageDescription={t('mltLabTest.header.subtitle')} />

            {isInitiating && (
                <MLTLabTestRecording onBackToListing={() => setIsInitiating(false)} testRecord={INITIAL_MLT_LAB_TEST_RECORDS[1]} />
            )}

            {!isInitiating && (
            <div className="min-h-screen bg-[#F8FAFC] pb-24 media-prep-scope">
                {/* Toast Notification */}
                <AnimatePresence>
                    {toastMessage && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className={`fixed top-5 right-6 z-50 px-5 py-3 rounded-xl shadow-xl flex items-center gap-3 border text-xs text-white ${toastMessage.type === 'success'
                                    ? 'bg-[#0F2942] border-emerald-500/50'
                                    : 'bg-[#0F2942] border-slate-700'
                                }`}
                        >
                            <span className={`material-symbols-outlined text-base ${toastMessage.type === 'success' ? 'text-emerald-400' : 'text-blue-400'
                                }`}>
                                {toastMessage.type === 'success' ? 'check_circle' : 'info'}
                            </span>
                            <div>
                                <p className="font-bold">{toastMessage.title}</p>
                                <p className="text-[11px] text-slate-300">{toastMessage.desc}</p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <main className="max-w-[1720px] mx-auto px-6 pt-6 space-y-6">


                    {/* ========================================================================= */}
                    {/* 2. FILTER CARD (matching exact fields in screenshot)                      */}
                    {/* ========================================================================= */}
                    <section className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">

                            {/* 1. Date Range */}

                                <div className="space-y-1.5">
                                    <label className="filterLabelLeft">From Date</label>
                                    <input
                                        type="date"
                                        value={fromdate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        className="form-control mt-2"
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="filterLabelLeft">To Date</label>
                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        className="form-control mt-2"
                                    />
                                </div>

                            {/* 2. Production Schedule ID */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                    {t('mltLabTest.fields.productionScheduleId')}
                                </label>
                                <select
                                    value={selectedSampleType}
                                    onChange={(e) => setSelectedSampleType(e.target.value)}
                                    className="w-full h-10 px-3.5 text-xs rounded-xl border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] font-medium cursor-pointer"
                                >
                                    <option value="All">{t('mltLabTest.options.all')}</option>
                                    <option value="Finished Product">{t('mltLabTest.options.sampleType.finishedProduct')}</option>
                                    <option value="Raw Material">{t('mltLabTest.options.sampleType.rawMaterial')}</option>
                                    <option value="In-process Material">{t('mltLabTest.options.sampleType.inProcessMaterial')}</option>
                                    <option value="Water Sample">{t('mltLabTest.options.sampleType.waterSample')}</option>
                                </select>
                            </div>

                            {/* 3. Sample Type */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                    {t('mltLabTest.fields.sampleType')}
                                </label>
                                <select
                                    value={selectedSampleType}
                                    onChange={(e) => setSelectedSampleType(e.target.value)}
                                    className="w-full h-10 px-3.5 text-xs rounded-xl border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] font-medium cursor-pointer"
                                >
                                    <option value="All">{t('mltLabTest.options.all')}</option>
                                    <option value="Finished Product">{t('mltLabTest.options.sampleType.finishedProduct')}</option>
                                    <option value="Raw Material">{t('mltLabTest.options.sampleType.rawMaterial')}</option>
                                    <option value="In-process Material">{t('mltLabTest.options.sampleType.inProcessMaterial')}</option>
                                    <option value="Water Sample">{t('mltLabTest.options.sampleType.waterSample')}</option>
                                </select>
                            </div>

                            {/* 4. Test Parameter */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                    {t('mltLabTest.fields.testParameter')}
                                </label>
                                <select
                                    value={selectedTestParameter}
                                    onChange={(e) => setSelectedTestParameter(e.target.value)}
                                    className="w-full h-10 px-3.5 text-xs rounded-xl border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] font-medium cursor-pointer"
                                >
                                    <option value="All">{t('mltLabTest.options.all')}</option>
                                    <option value="TAMC">TAMC</option>
                                    <option value="TYMC">TYMC</option>
                                    <option value="Specified Microorganisms">{t('mltLabTest.options.testParameter.specifiedMicroorganisms')}</option>
                                </select>
                            </div>

                            {/* 5. Status */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                    {t('mltLabTest.fields.status')}
                                </label>
                                <select
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    className="w-full h-10 px-3.5 text-xs rounded-xl border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] font-medium cursor-pointer"
                                >
                                    <option value="All">{t('mltLabTest.options.all')}</option>
                                    <option value="Pending">{t('mltLabTest.options.status.pending')}</option>
                                    <option value="In Progress">{t('mltLabTest.options.status.inProgress')}</option>
                                    <option value="Completed">{t('mltLabTest.options.status.completed')}</option>
                                </select>
                            </div>
                        </div>


                    </section>

                    {/* ========================================================================= */}
                    {/* 3. STATUS TABS & RECORD COUNTER BAR (matching screenshot)                 */}
                    {/* ========================================================================= */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200">

                        {/* Status Tabs with exact labels and counts */}
                        <div className="flex items-center gap-8 -mb-px">
                            <button
                                onClick={() => {
                                    setActiveTab('To Be Tested');
                                    // setCurrentPage(1);
                                }}
                                className={`pb-3 text-xs font-bold transition-all relative cursor-pointer ${activeTab === 'To Be Tested'
                                        ? 'text-[#0052CC] border-b-2 border-[#0052CC]'
                                        : 'text-slate-600 hover:text-slate-900'
                                    }`}
                            >
                                {t('mltLabTest.tabs.toBeTested', { num: tabCounts.toBeTested })}
                            </button>

                            <button
                                onClick={() => {
                                    setActiveTab('In Progress');
                                    // setCurrentPage(1);
                                }}
                                className={`pb-3 text-xs font-bold transition-all relative cursor-pointer ${activeTab === 'In Progress'
                                        ? 'text-[#0052CC] border-b-2 border-[#0052CC]'
                                        : 'text-slate-600 hover:text-slate-900'
                                    }`}
                            >
                                {t('mltLabTest.tabs.inProgress', { num: tabCounts.inProgress })}
                            </button>

                            <button
                                onClick={() => {
                                    setActiveTab('Completed');
                                    // setCurrentPage(1);
                                }}
                                className={`pb-3 text-xs font-bold transition-all relative cursor-pointer ${activeTab === 'Completed'
                                        ? 'text-[#0052CC] border-b-2 border-[#0052CC]'
                                        : 'text-slate-600 hover:text-slate-900'
                                    }`}
                            >
                                {t('mltLabTest.tabs.completed', { num: tabCounts.completed })}
                            </button>
                        </div>

                    </div>

                    {/* ========================================================================= */}
                    {/* 4. TABLE SECTION (matching columns in screenshot)                         */}
                    {/* ========================================================================= */}
                    <section className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
                        <div className="overflow-x-auto h-[60vh]">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="tableHeadRow text-center">
                                        <th className="tableHeadData2">{t('mltLabTest.table.serialNo')}</th>
                                        <th className="tableHeadData2">{t('mltLabTest.table.mltTestId')}</th>
                                        <th className="tableHeadData2">{t('mltLabTest.fields.productionScheduleId')}</th>
                                        <th className="tableHeadData2">{t('mltLabTest.fields.sampleType')}</th>
                                        <th className="tableHeadData2">{t('mltLabTest.fields.productOrMaterial')}</th>
                                        <th className="tableHeadData2">{t('mltLabTest.fields.testParameter')}</th>
                                        <th className="tableHeadData2">{t('mltLabTest.table.medium')}</th>
                                        <th className="tableHeadData2 text-center">{t('mltLabTest.table.numberOfPlates')}</th>
                                        <th className="tableHeadData2 text-center">{t('mltLabTest.fields.status')}</th>
                                        <th className="tableHeadData2">{t('mltLabTest.table.assignedDate')}</th>
                                        <th className="tableHeadData2">{t('mltLabTest.table.action')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-xs">
                                    {paginatedRecords.length === 0 ? (
                                        <tr>
                                            <td colSpan={11} className="py-12 text-center text-slate-400">
                                                <span className="material-symbols-outlined text-3xl mb-2 text-slate-300 block">
                                                    search_off
                                                </span>
                                                <p className="font-bold text-sm text-slate-600">{t('mltLabTest.table.emptyTitle')}</p>
                                                <p className="text-xs text-slate-400 mt-0.5">{t('mltLabTest.table.emptyHint')}</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedRecords.map((item, idx) => {
                                            const rowNumber = (page - 1) * pageSize + idx + 1;
                                            return (
                                                <tr
                                                    key={item.id}
                                                    // onClick={() => handleOpenDetails(item)}
                                                    className="hover:bg-blue-50/30 transition-colors group cursor-pointer"
                                                >
                                                    {/* 1. S.No. */}
                                                    <td className="py-3.5 px-3 text-center text-slate-500 font-medium">
                                                        {rowNumber}
                                                    </td>

                                                    {/* 2. MLT Test ID (clickable link style) */}
                                                    <td className="py-3.5 px-3.5 font-medium whitespace-nowrap">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDetails(item);
                                                            }}
                                                            className="text-[#0052CC] hover:underline font-semibold text-left cursor-pointer"
                                                            title={t('mltLabTest.table.viewDetailsTooltip')}
                                                        >
                                                            {item.mltTestId}
                                                        </button>
                                                    </td>

                                                    {/* 3. Production Schedule ID */}
                                                    <td className="py-3.5 px-3.5 font-mono text-slate-800 text-[11px] whitespace-nowrap">
                                                        {item.productionSchedule}
                                                    </td>

                                                    {/* 4. Sample Type */}
                                                    <td className="py-3.5 px-3.5 whitespace-nowrap text-slate-800 font-medium">
                                                        {item.sampleType}
                                                    </td>

                                                    {/* 5. Product / Material */}
                                                    <td className="py-3.5 px-3.5 font-medium text-slate-900 max-w-[220px] truncate" title={item.productOrMaterial}>
                                                        {item.productOrMaterial}
                                                    </td>

                                                    {/* 6. Test Parameter */}
                                                    <td className="py-3.5 px-3 font-semibold text-slate-800 whitespace-nowrap">
                                                        {item.testParameter}
                                                    </td>

                                                    {/* 7. Medium */}
                                                    <td className="py-3.5 px-3 text-slate-700 whitespace-nowrap">
                                                        {item.medium}
                                                    </td>

                                                    {/* 8. No. of Plates */}
                                                    <td className="py-3.5 px-3 text-center font-semibold text-slate-800">
                                                        {item.numberOfPlates}
                                                    </td>

                                                    {/* 9. Status Badge */}
                                                    <td className="py-3.5 px-3 text-center whitespace-nowrap">
                                                        <span
                                                            className={`inline-flex items-center px-3 py-0.5 rounded-full text-[11px] font-semibold ${item.status === 'Completed'
                                                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                                    : item.status === 'In Progress'
                                                                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                                                        : 'bg-slate-100 text-slate-600 border border-slate-200'
                                                                }`}
                                                        >
                                                            {item.status}
                                                        </span>
                                                    </td>

                                                    {/* 10. Assigned Date */}
                                                    <td className="py-3.5 px-3.5 whitespace-nowrap text-slate-700 text-[11px]">
                                                        {item.assignedDate}
                                                    </td>

                                                    {/* 11. Action (Start / Continue / View) */}
                                                    <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                                        <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                                            {item.status === 'Pending' && (
                                                                <button
                                                                    // onClick={() => {
                                                                    //     if (onStartRecording) {
                                                                    //         onStartRecording(item);
                                                                    //         setIsInitiating(true)
                                                                    //     } else {
                                                                    //         handleOpenDetails(item);
                                                                    //     }
                                                                    // }}
                                                                    onClick={() => setIsInitiating(true)}
                                                                    className="px-3 py-1 rounded-lg border border-[#0052CC] text-[#0052CC] hover:bg-blue-50 font-medium text-xs flex items-center gap-1.5 transition-colors cursor-pointer"
                                                                    title={t('mltLabTest.table.startTooltip')}
                                                                >
                                                                    <span className="material-symbols-outlined text-xs">science</span>
                                                                    {t('mltLabTest.table.start')}
                                                                </button>
                                                            )}

                                                            {item.status === 'In Progress' && (
                                                                <button
                                                                    onClick={() => {
                                                                        if (onStartRecording) {
                                                                            onStartRecording(item);
                                                                        } else {
                                                                            handleOpenDetails(item);
                                                                        }
                                                                    }}
                                                                    className="px-3 py-1 rounded-lg border border-[#0052CC] text-[#0052CC] hover:bg-blue-50 font-medium text-xs flex items-center gap-1.5 transition-colors cursor-pointer"
                                                                    title={t('mltLabTest.table.continueTooltip')}
                                                                >
                                                                    <span className="material-symbols-outlined text-xs">science</span>
                                                                    {t('mltLabTest.table.continue')}
                                                                </button>
                                                            )}

                                                            {item.status === 'Completed' && (
                                                                <button
                                                                    onClick={() => handleOpenDetails(item)}
                                                                    className="px-3 py-1 rounded-lg border border-[#0052CC] text-[#0052CC] hover:bg-blue-50 font-medium text-xs flex items-center gap-1.5 transition-colors cursor-pointer"
                                                                    title={t('mltLabTest.table.viewTooltip')}
                                                                >
                                                                    <span className="material-symbols-outlined text-xs">description</span>
                                                                    {t('mltLabTest.table.view')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            handlePageChange={handlePageChange}
                        />
                    </section>
                </main>

                {/* ========================================================================= */}
                {/* 5. MLT TEST DETAILS MODAL (ORGANIZED BASED ON LAB STATUS)                  */}
                {/* ========================================================================= */}
                <AnimatePresence>
                    {isDetailsModalOpen && selectedRecord && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto">
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 my-8 overflow-hidden flex flex-col max-h-[90vh]"
                            >
                                {/* Modal Top Bar */}
                                <div className="bg-[#0F2942] text-white p-4 px-6 flex items-center justify-between shrink-0">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-300 flex items-center justify-center">
                                            <span className="material-symbols-outlined text-base">
                                                {selectedRecord.status === 'Completed' ? 'verified' : selectedRecord.status === 'In Progress' ? 'science' : 'biotech'}
                                            </span>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h2 className="text-sm font-bold tracking-tight text-white">
                                                    {t('mltLabTest.modal.title', { testId: selectedRecord.mltTestId })}
                                                </h2>
                                                <span className="text-slate-400 text-xs">•</span>
                                                <span className="text-xs text-slate-300 font-mono">{selectedRecord.sampleId}</span>
                                            </div>
                                            <p className="text-[11px] text-slate-400">
                                                {t('mltLabTest.modal.subtitle', { parameter: selectedRecord.testParameter, medium: selectedRecord.medium, product: selectedRecord.productOrMaterial })}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {/* Status Badge */}
                                        <span
                                            className={`px-3 py-0.5 rounded-full text-xs font-semibold ${selectedRecord.status === 'Completed'
                                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                                    : selectedRecord.status === 'In Progress'
                                                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                                                        : 'bg-slate-700 text-slate-300 border border-slate-600'
                                                }`}
                                        >
                                            {selectedRecord.status}
                                        </span>

                                        <button
                                            onClick={() => setIsDetailsModalOpen(false)}
                                            className="w-8 h-8 rounded-lg bg-slate-800 text-slate-300 hover:text-white flex items-center justify-center cursor-pointer transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-sm">close</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Modal Scrollable Body */}
                                <div className="p-6 space-y-6 overflow-y-auto flex-1 text-slate-800">

                                    {/* ------------------------------------------------------------- */}
                                    {/* LAB STATUS SPECIFIC BANNER                                     */}
                                    {/* ------------------------------------------------------------- */}
                                    {selectedRecord.status === 'Pending' && (
                                        <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                                            <span className="material-symbols-outlined text-amber-600 text-xl mt-0.5 shrink-0">
                                                pending_actions
                                            </span>
                                            <div className="text-xs">
                                                <p className="font-bold text-amber-900">{t('mltLabTest.modal.banner.pendingTitle')}</p>
                                                <p className="text-amber-800 mt-0.5 leading-relaxed">
                                                    <Trans i18nKey="mltLabTest.modal.banner.pendingBody" components={{ strong: <strong /> }} />
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {selectedRecord.status === 'In Progress' && (
                                        <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                                            <span className="material-symbols-outlined text-[#0052CC] text-xl mt-0.5 shrink-0 animate-pulse">
                                                autorenew
                                            </span>
                                            <div className="text-xs">
                                                <p className="font-bold text-blue-950">{t('mltLabTest.modal.banner.inProgressTitle')}</p>
                                                <p className="text-blue-900 mt-0.5 leading-relaxed">
                                                    <Trans
                                                        i18nKey="mltLabTest.modal.banner.inProgressBody"
                                                        values={{ chamber: selectedRecord.incubatorChamber || 'Incubator INC-MB-02', temp: selectedRecord.actualIncubationTemp || '32.4°C' }}
                                                        components={{ strong: <strong /> }}
                                                    />
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {selectedRecord.status === 'Completed' && (
                                        <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
                                            <span className="material-symbols-outlined text-emerald-600 text-xl mt-0.5 shrink-0">
                                                verified
                                            </span>
                                            <div className="text-xs">
                                                <p className="font-bold text-emerald-950">{t('mltLabTest.modal.banner.completedTitle')}</p>
                                                <p className="text-emerald-900 mt-0.5 leading-relaxed">
                                                    {t('mltLabTest.modal.banner.completedBody')}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* ------------------------------------------------------------- */}
                                    {/* COMMON SECTION: SAMPLE & MATERIAL IDENTIFICATION               */}
                                    {/* ------------------------------------------------------------- */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                                            <span className="material-symbols-outlined text-sm text-[#0052CC]">biotech</span>
                                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                                                {t('mltLabTest.modal.traceability.title')}
                                            </h3>
                                        </div>

                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl bg-slate-50/80 border border-slate-200 text-xs">
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.fields.productionScheduleId')}</span>
                                                <span className="font-mono font-bold text-blue-700 mt-0.5 block">{selectedRecord.productionSchedule}</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.fields.sampleId')}</span>
                                                <span className="font-mono font-semibold text-slate-800 mt-0.5 block">{selectedRecord.sampleId}</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.fields.productOrMaterial')}</span>
                                                <span className="font-bold text-slate-900 mt-0.5 block truncate" title={selectedRecord.productOrMaterial}>
                                                    {selectedRecord.productOrMaterial}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.fields.sampleType')}</span>
                                                <span className="font-semibold text-slate-800 mt-0.5 block">{selectedRecord.sampleType}</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.fields.batchNo')}</span>
                                                <span className="font-mono font-bold text-blue-700 mt-0.5 block">{selectedRecord.batchNo}</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.fields.arNumber')}</span>
                                                <span className="font-mono font-semibold text-slate-800 mt-0.5 block">{selectedRecord.arNumber}</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.fields.samplingDate')}</span>
                                                <span className="font-semibold text-slate-800 mt-0.5 block">{selectedRecord.samplingDate}</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.fields.quantitySampledUsed')}</span>
                                                <span className="font-semibold text-slate-800 mt-0.5 block">{selectedRecord.quantitySampled} / {selectedRecord.quantityUsedForTest}</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.fields.assignedAnalyst')}</span>
                                                <span className="font-bold text-slate-900 mt-0.5 block">{selectedRecord.assignedAnalyst}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ------------------------------------------------------------- */}
                                    {/* STATUS SPECIFIC SECTION: PRE-TEST & INCUBATION SETUP (PENDING) */}
                                    {/* ------------------------------------------------------------- */}
                                    {selectedRecord.status === 'Pending' && (
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                                                <span className="material-symbols-outlined text-sm text-[#0052CC]">tune</span>
                                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                                                    {t('mltLabTest.modal.pending.title')}
                                                </h3>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                                                <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-1">
                                                    <span className="text-[10px] font-bold uppercase text-slate-400 block">{t('mltLabTest.fields.testParameter')}</span>
                                                    <span className="font-bold text-[#0F2942] text-sm block">{selectedRecord.testParameter}</span>
                                                    <span className="text-slate-500 text-[11px] block">{t('mltLabTest.modal.pending.medium', { medium: selectedRecord.medium })}</span>
                                                </div>
                                                <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-1">
                                                    <span className="text-[10px] font-bold uppercase text-slate-400 block">{t('mltLabTest.modal.pending.requiredIncubation')}</span>
                                                    <span className="font-bold text-slate-900 text-sm block">{selectedRecord.targetIncubationTemp}</span>
                                                    <span className="text-slate-500 text-[11px] block">{t('mltLabTest.modal.pending.duration', { duration: selectedRecord.incubationDuration })}</span>
                                                </div>
                                                <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-1">
                                                    <span className="text-[10px] font-bold uppercase text-slate-400 block">{t('mltLabTest.modal.pending.platingMethod')}</span>
                                                    <span className="font-bold text-slate-900 text-sm block">{selectedRecord.testMethod}</span>
                                                    <span className="text-slate-500 text-[11px] block">{t('mltLabTest.modal.pending.platesRequired', { plates: selectedRecord.numberOfPlates })}</span>
                                                </div>
                                            </div>

                                            {/* Pre-allocated Plates Checklist */}
                                            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-bold text-slate-700">{t('mltLabTest.modal.pending.checklistTitle')}</span>
                                                    <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                                        {t('mltLabTest.modal.pending.preSterilityVerified')}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 text-xs">
                                                    {selectedRecord.plateReadings.map((p) => (
                                                        <div key={p.plateNo} className="p-2.5 rounded-lg bg-white border border-slate-200">
                                                            <span className="font-bold text-slate-700 block">{t('mltLabTest.modal.plateLabel', { plateNo: p.plateNo })}</span>
                                                            <span className="font-mono text-[10px] text-slate-500 block">{p.serialNo}</span>
                                                            <span className="text-[10px] text-emerald-600 font-semibold mt-1 flex items-center gap-1">
                                                                <span className="material-symbols-outlined text-[12px]">check_circle</span>
                                                                {t('mltLabTest.modal.pending.ready')}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Incubator Assignment Control */}
                                            <div className="p-4 rounded-xl border border-blue-200 bg-blue-50/50 space-y-3">
                                                <span className="text-xs font-bold text-blue-950 block">{t('mltLabTest.modal.pending.selectChamber')}</span>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                                                    <div>
                                                        <label className="text-[11px] font-semibold text-slate-700 mb-1 block">{t('mltLabTest.modal.pending.incubatorUnit')}</label>
                                                        <select
                                                            value={selectedIncubator}
                                                            onChange={(e) => setSelectedIncubator(e.target.value)}
                                                            className="w-full h-9 px-3 text-xs rounded-lg border border-slate-300 bg-white text-slate-800 font-medium cursor-pointer"
                                                        >
                                                            <option value="Incubator INC-MB-01 (Chamber A - 32.5°C)">Incubator INC-MB-01 (Chamber A - 32.5°C)</option>
                                                            <option value="Incubator INC-MB-02 (Chamber A - 32.3°C)">Incubator INC-MB-02 (Chamber A - 32.3°C)</option>
                                                            <option value="Incubator INC-MB-03 (Chamber B - 22.4°C)">Incubator INC-MB-03 (Chamber B - 22.4°C)</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="text-[11px] font-semibold text-slate-700 mb-1 block">{t('mltLabTest.modal.pending.dilutionSpecification')}</label>
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            value={selectedRecord.dilutionFactor}
                                                            className="w-full h-9 px-3 text-xs rounded-lg border border-slate-200 bg-slate-100 text-slate-600 font-mono"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* ------------------------------------------------------------- */}
                                    {/* STATUS SPECIFIC SECTION: ACTIVE INCUBATION & INTERIM READINGS */}
                                    {/* ------------------------------------------------------------- */}
                                    {selectedRecord.status === 'In Progress' && (
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                                                <span className="material-symbols-outlined text-sm text-[#0052CC]">hourglass_bottom</span>
                                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                                                    {t('mltLabTest.modal.inProgress.title')}
                                                </h3>
                                            </div>

                                            {/* Incubation Telemetry Bar */}
                                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs bg-slate-50 p-4 rounded-xl border border-slate-200">
                                                <div>
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.modal.inProgress.incubatorChamber')}</span>
                                                    <span className="font-bold text-slate-800 mt-0.5 block">{selectedRecord.incubatorChamber || 'INC-MB-02'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.modal.inProgress.currentChamberTemp')}</span>
                                                    <span className="font-mono font-bold text-emerald-700 mt-0.5 block">{selectedRecord.actualIncubationTemp || '32.4°C'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.modal.inProgress.startedTimestamp')}</span>
                                                    <span className="font-mono text-slate-700 mt-0.5 block">{selectedRecord.incubationStartTime || '10-Oct-2026 11:30 AM'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.modal.inProgress.expectedReadout')}</span>
                                                    <span className="font-mono font-bold text-blue-700 mt-0.5 block">{selectedRecord.expectedReadingDate || '13-Oct-2026 11:30 AM'}</span>
                                                </div>
                                            </div>

                                            {/* Plate-by-Plate Interim Count Entry Table */}
                                            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                                                <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
                                                    <span className="text-xs font-bold text-slate-800">
                                                        {t('mltLabTest.modal.inProgress.readingsTitle')}
                                                    </span>
                                                    <span className="text-[11px] font-semibold text-slate-500">
                                                        {t('mltLabTest.modal.inProgress.testPlatesPlusControl', { plates: selectedRecord.numberOfPlates })}
                                                    </span>
                                                </div>

                                                <table className="w-full text-left text-xs border-collapse">
                                                    <thead>
                                                        <tr className="bg-slate-100 text-slate-700 uppercase text-[9px] font-bold border-b border-slate-200">
                                                            <th className="py-2.5 px-3 w-16 text-center">{t('mltLabTest.modal.tableHeaders.plateNo')}</th>
                                                            <th className="py-2.5 px-3">{t('mltLabTest.modal.tableHeaders.plateSerialNo')}</th>
                                                            <th className="py-2.5 px-3 w-32">{t('mltLabTest.modal.tableHeaders.observedCfu')}</th>
                                                            <th className="py-2.5 px-3">{t('mltLabTest.modal.tableHeaders.colonyMorphologyNotes')}</th>
                                                            <th className="py-2.5 px-3 text-center w-28">{t('mltLabTest.fields.status')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100">
                                                        {selectedRecord.plateReadings.map((p) => (
                                                            <tr key={p.plateNo} className="hover:bg-slate-50">
                                                                <td className="py-2.5 px-3 text-center font-bold text-slate-600">{t('mltLabTest.modal.plateLabel', { plateNo: p.plateNo })}</td>
                                                                <td className="py-2.5 px-3 font-mono text-slate-700">{p.serialNo}</td>
                                                                <td className="py-2 px-3">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        value={editingPlateCounts[p.plateNo] !== undefined ? editingPlateCounts[p.plateNo] : (p.cfu === '—' ? '' : p.cfu)}
                                                                        onChange={(e) => setEditingPlateCounts({
                                                                            ...editingPlateCounts,
                                                                            [p.plateNo]: e.target.value
                                                                        })}
                                                                        placeholder="0"
                                                                        className="w-24 h-8 px-2 text-xs font-bold font-mono rounded border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#0052CC]"
                                                                    />
                                                                </td>
                                                                <td className="py-2.5 px-3 text-slate-600 text-[11px]">
                                                                    {p.morphology || t('mltLabTest.modal.inProgress.defaultMorphology')}
                                                                </td>
                                                                <td className="py-2.5 px-3 text-center">
                                                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700">
                                                                        {t('mltLabTest.modal.inProgress.logged')}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}

                                                        {/* Negative Media Control Row */}
                                                        <tr className="bg-slate-50/70 font-semibold">
                                                            <td className="py-2.5 px-3 text-center text-slate-500">{t('mltLabTest.modal.inProgress.control')}</td>
                                                            <td className="py-2.5 px-3 font-mono text-slate-600">NEG-CTL-SCDA-01</td>
                                                            <td className="py-2.5 px-3 font-mono text-emerald-700 font-bold">0 CFU</td>
                                                            <td className="py-2.5 px-3 text-slate-500 text-[11px]">{t('mltLabTest.modal.inProgress.controlNoGrowth')}</td>
                                                            <td className="py-2.5 px-3 text-center">
                                                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700">
                                                                    {t('mltLabTest.modal.inProgress.valid')}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* ------------------------------------------------------------- */}
                                    {/* STATUS SPECIFIC SECTION: FINAL QUANTITATIVE RESULTS (COMPLETED) */}
                                    {/* ------------------------------------------------------------- */}
                                    {selectedRecord.status === 'Completed' && (
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                                                <span className="material-symbols-outlined text-sm text-emerald-600">task_alt</span>
                                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                                                    {t('mltLabTest.modal.completed.title')}
                                                </h3>
                                            </div>

                                            {/* Result Summary Banner */}
                                            <div className="p-4 rounded-xl bg-emerald-50/80 border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                <div>
                                                    <span className="text-[10px] font-bold uppercase text-emerald-800 tracking-wider block">
                                                        {t('mltLabTest.modal.completed.finalCertifiedResult')}
                                                    </span>
                                                    <div className="flex items-center gap-3 mt-1">
                                                        <span className="text-xl font-black text-emerald-900 font-mono">
                                                            {selectedRecord.calculatedBioburden}
                                                        </span>
                                                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-600 text-white shadow-xs">
                                                            {t('mltLabTest.modal.completed.passesSpecification')}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-emerald-800 mt-1">
                                                        <Trans
                                                            i18nKey="mltLabTest.modal.completed.acceptanceCriteria"
                                                            values={{ limit: selectedRecord.acceptanceLimit }}
                                                            components={{ strong: <strong /> }}
                                                        />
                                                    </p>
                                                </div>

                                                <div className="text-right text-xs space-y-1">
                                                    <div>
                                                        <span className="text-slate-500 font-semibold">{t('mltLabTest.modal.completed.averageCount')}{' '}</span>
                                                        <span className="font-bold text-slate-900 font-mono">{t('mltLabTest.modal.completed.cfuPerPlate', { value: selectedRecord.averageCfuPerPlate })}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 font-semibold">{t('mltLabTest.modal.completed.negativeControl')}{' '}</span>
                                                        <span className="font-bold text-emerald-700 font-mono">{t('mltLabTest.modal.completed.negativeControlResult', { value: selectedRecord.negativeControlCfu })}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 font-semibold">{t('mltLabTest.modal.completed.completedOn')}{' '}</span>
                                                        <span className="font-mono text-slate-800">{selectedRecord.completionDate}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Final Plates Enumeration Table */}
                                            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                                                <table className="w-full text-left text-xs border-collapse">
                                                    <thead>
                                                        <tr className="bg-slate-100 text-slate-700 uppercase text-[9px] font-bold border-b border-slate-200">
                                                            <th className="py-2.5 px-3 w-16 text-center">{t('mltLabTest.modal.tableHeaders.plateNo')}</th>
                                                            <th className="py-2.5 px-3">{t('mltLabTest.modal.tableHeaders.plateSerialNo')}</th>
                                                            <th className="py-2.5 px-3 text-center">{t('mltLabTest.modal.tableHeaders.finalCfuCount')}</th>
                                                            <th className="py-2.5 px-3">{t('mltLabTest.modal.tableHeaders.observedColonyCharacteristics')}</th>
                                                            <th className="py-2.5 px-3 text-center">{t('mltLabTest.modal.tableHeaders.resultStatus')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100">
                                                        {selectedRecord.plateReadings.map((p) => (
                                                            <tr key={p.plateNo} className="hover:bg-slate-50">
                                                                <td className="py-2 px-3 text-center font-bold text-slate-600">{t('mltLabTest.modal.plateLabel', { plateNo: p.plateNo })}</td>
                                                                <td className="py-2 px-3 font-mono text-slate-700">{p.serialNo}</td>
                                                                <td className="py-2 px-3 text-center font-bold font-mono text-slate-900 text-sm">
                                                                    {p.cfu}
                                                                </td>
                                                                <td className="py-2 px-3 text-slate-600 text-[11px]">{p.morphology || t('mltLabTest.modal.completed.defaultMorphology')}</td>
                                                                <td className="py-2 px-3 text-center">
                                                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700">
                                                                        {t('mltLabTest.modal.completed.verified')}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Specified Organisms (if any) */}
                                            {selectedRecord.specifiedOrganisms && (
                                                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                                                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 text-xs font-bold text-slate-800">
                                                        {t('mltLabTest.modal.completed.specifiedTitle')}
                                                    </div>
                                                    <table className="w-full text-left text-xs border-collapse">
                                                        <thead>
                                                            <tr className="bg-slate-100 text-slate-700 uppercase text-[9px] font-bold border-b border-slate-200">
                                                                <th className="py-2 px-3">{t('mltLabTest.modal.tableHeaders.targetMicroorganism')}</th>
                                                                <th className="py-2 px-3">{t('mltLabTest.modal.tableHeaders.selectiveMedium')}</th>
                                                                <th className="py-2 px-3 text-center">{t('mltLabTest.modal.tableHeaders.observation')}</th>
                                                                <th className="py-2 px-3 text-center">{t('mltLabTest.modal.tableHeaders.result')}</th>
                                                                <th className="py-2 px-3 text-center">{t('mltLabTest.modal.tableHeaders.compliance')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100 text-[11px]">
                                                            {selectedRecord.specifiedOrganisms.map((org, i) => (
                                                                <tr key={i}>
                                                                    <td className="py-2 px-3 font-bold text-slate-900 italic">{org.organismName}</td>
                                                                    <td className="py-2 px-3 text-slate-600">{org.selectiveMedium}</td>
                                                                    <td className="py-2 px-3 text-center text-slate-600">{org.colonyObservation}</td>
                                                                    <td className="py-2 px-3 text-center font-bold text-emerald-700">{org.result}</td>
                                                                    <td className="py-2 px-3 text-center font-bold text-emerald-700">{org.complianceStatus}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            {/* Electronic Sign-off Record */}
                                            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.modal.completed.testedByAnalyst')}</span>
                                                    <span className="font-bold text-slate-900 mt-0.5 block">{selectedRecord.analystSignOff?.name || 'Dr. Sarah Jenkins'}</span>
                                                    <span className="text-slate-500 text-[11px]">{selectedRecord.analystSignOff?.signedAt || '12-Oct-2026 14:15'}</span>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">{t('mltLabTest.modal.completed.qcReview')}</span>
                                                    <span className="font-bold text-slate-900 mt-0.5 block">{selectedRecord.qcReviewSignOff?.name || 'Emily Watson (QC Manager)'}</span>
                                                    <span className="text-emerald-700 font-bold text-[11px]">{t('mltLabTest.modal.completed.approvedForBatchRelease')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Remarks Field (Editable in Pending & In Progress) */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-700">{t('mltLabTest.modal.remarks.label')}</label>
                                        {selectedRecord.status === 'Completed' ? (
                                            <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700">
                                                {selectedRecord.remarks || t('mltLabTest.modal.remarks.noAdverse')}
                                            </div>
                                        ) : (
                                            <textarea
                                                rows={2}
                                                value={editingRemarks}
                                                onChange={(e) => setEditingRemarks(e.target.value)}
                                                placeholder={t('mltLabTest.modal.remarks.placeholder')}
                                                className="w-full p-3 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]"
                                            />
                                        )}
                                    </div>

                                </div>

                                {/* Modal Footer with Actions based on Lab Status */}
                                <div className="bg-slate-50 border-t border-slate-200 p-4 px-6 flex items-center justify-between shrink-0">
                                    <button
                                        onClick={() => setIsDetailsModalOpen(false)}
                                        className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 hover:bg-white text-xs font-bold transition-colors cursor-pointer"
                                    >
                                        {t('mltLabTest.modal.footer.close')}
                                    </button>

                                    <div className="flex items-center gap-3">
                                        {/* PENDING ACTIONS */}
                                        {selectedRecord.status === 'Pending' && (
                                            <button
                                                onClick={() => {
                                                    setIsDetailsModalOpen(false);
                                                    if (onStartRecording) {
                                                        onStartRecording(selectedRecord);
                                                    } else {
                                                        handleStartTest(selectedRecord);
                                                    }
                                                }}
                                                className="px-5 py-2 rounded-xl bg-[#0052CC] hover:bg-[#0043A8] text-white text-xs font-bold flex items-center gap-2 transition-colors shadow-xs cursor-pointer"
                                            >
                                                <span className="material-symbols-outlined text-sm">science</span>
                                                {t('mltLabTest.modal.footer.startLabTest')}
                                            </button>
                                        )}

                                        {/* IN PROGRESS ACTIONS */}
                                        {selectedRecord.status === 'In Progress' && (
                                            <>
                                                <button
                                                    onClick={() => {
                                                        setIsDetailsModalOpen(false);
                                                        setIsInitiating(true);
                                                        if (onStartRecording) {
                                                            onStartRecording(selectedRecord);
                                                        }
                                                    }}
                                                    className="px-4 py-2 rounded-xl bg-[#0052CC] hover:bg-[#0043A8] text-white text-xs font-bold flex items-center gap-1.5 transition-colors shadow-xs cursor-pointer"
                                                >
                                                    <span className="material-symbols-outlined text-sm">edit_square</span>
                                                    {t('mltLabTest.modal.footer.openRecordingScreen')}
                                                </button>

                                                <button
                                                    onClick={handleCompleteTest}
                                                    className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-2 transition-colors shadow-xs cursor-pointer"
                                                >
                                                    <span className="material-symbols-outlined text-sm">task_alt</span>
                                                    {t('mltLabTest.modal.footer.completeAndCertify')}
                                                </button>
                                            </>
                                        )}

                                        {/* COMPLETED ACTIONS */}
                                        {selectedRecord.status === 'Completed' && (
                                            <>
                                                <button
                                                    onClick={() => setShowPrintModal(true)}
                                                    className="px-4 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                                                >
                                                    <span className="material-symbols-outlined text-sm">print</span>
                                                    {t('mltLabTest.modal.footer.printReport')}
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        showToast(t('mltLabTest.toast.pdfExport.title'), t('mltLabTest.toast.pdfExport.desc', { testId: selectedRecord.mltTestId }), 'success');
                                                        setShowPrintModal(true);
                                                    }}
                                                    className="px-4 py-2 rounded-xl bg-[#0052CC] hover:bg-[#0043A8] text-white text-xs font-bold flex items-center gap-2 transition-colors shadow-xs cursor-pointer"
                                                >
                                                    <span className="material-symbols-outlined text-sm">picture_as_pdf</span>
                                                    {t('mltLabTest.modal.footer.downloadPdf')}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>

                {/* ========================================================================= */}
                {/* 6. PRINT / PDF REPORT MODAL FOR COMPLETED TEST                            */}
                {/* ========================================================================= */}
                <AnimatePresence>
                    {showPrintModal && selectedRecord && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto">
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 my-8 overflow-hidden"
                            >
                                {/* Modal Bar */}
                                <div className="bg-[#0F2942] text-white p-4 flex items-center justify-between print:hidden">
                                    <div className="flex items-center gap-2 text-xs font-bold">
                                        <span className="material-symbols-outlined text-sm text-blue-400">picture_as_pdf</span>
                                        {t('mltLabTest.print.barTitle', { testId: selectedRecord.mltTestId })}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => window.print()}
                                            className="px-4 py-1.5 rounded-lg bg-[#0052CC] hover:bg-blue-600 text-white font-bold text-xs flex items-center gap-1.5 cursor-pointer"
                                        >
                                            <span className="material-symbols-outlined text-sm">print</span>
                                            {t('mltLabTest.print.printSavePdf')}
                                        </button>
                                        <button
                                            onClick={() => setShowPrintModal(false)}
                                            className="w-8 h-8 rounded-lg bg-slate-800 text-slate-300 hover:text-white flex items-center justify-center cursor-pointer"
                                        >
                                            <span className="material-symbols-outlined text-sm">close</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Printable Body */}
                                <div className="p-8 space-y-6 text-slate-800 font-['Inter'] print:p-0">
                                    <div className="border-b-2 border-[#0F2942] pb-4 flex items-start justify-between">
                                        <div>
                                            <h2 className="text-xl font-black text-[#0F2942] tracking-tight uppercase">
                                                {t('mltLabTest.print.title')}
                                            </h2>
                                            <p className="text-xs text-slate-500 font-semibold mt-0.5">
                                                {t('mltLabTest.print.subtitle')}
                                            </p>
                                            <div className="flex items-center gap-3 text-[10px] text-slate-600 font-bold mt-2">
                                                <span>{t('mltLabTest.print.testId', { value: selectedRecord.mltTestId })}</span>
                                                <span>•</span>
                                                <span>{t('mltLabTest.print.scheduleId', { value: selectedRecord.productionSchedule })}</span>
                                                <span>•</span>
                                                <span>{t('mltLabTest.print.sampleId', { value: selectedRecord.sampleId })}</span>
                                                <span>•</span>
                                                <span>{t('mltLabTest.print.arNo', { value: selectedRecord.arNumber })}</span>
                                            </div>
                                        </div>
                                        <div className="text-right text-xs">
                                            <span className="px-2.5 py-1 font-black rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px] uppercase">
                                                {t('mltLabTest.print.passesSpecification')}
                                            </span>
                                            <p className="text-[10px] text-slate-400 mt-1">{t('mltLabTest.print.pageOneOfOne')}</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 text-xs bg-slate-50 p-4 rounded-lg border border-slate-200">
                                        <div>
                                            <span className="font-bold text-slate-500 block">{t('mltLabTest.print.productOrMaterial')}</span>
                                            <span className="font-semibold text-slate-900">{selectedRecord.productOrMaterial}</span>
                                        </div>
                                        <div>
                                            <span className="font-bold text-slate-500 block">{t('mltLabTest.print.batchNo')}</span>
                                            <span className="font-semibold text-slate-900">{selectedRecord.batchNo}</span>
                                        </div>
                                        <div>
                                            <span className="font-bold text-slate-500 block">{t('mltLabTest.print.testParameter')}</span>
                                            <span className="font-semibold text-slate-900">{selectedRecord.testParameter} ({selectedRecord.medium})</span>
                                        </div>
                                        <div>
                                            <span className="font-bold text-slate-500 block">{t('mltLabTest.print.incubationPeriod')}</span>
                                            <span className="font-semibold text-slate-900">{t('mltLabTest.print.incubationRange', { start: selectedRecord.incubationStartTime, end: selectedRecord.completionDate })}</span>
                                        </div>
                                    </div>

                                    <div>
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 mb-2">
                                            {t('mltLabTest.print.plateEnumerationReadings')}
                                        </h4>
                                        <table className="w-full text-left text-xs border border-slate-200 border-collapse">
                                            <thead>
                                                <tr className="bg-slate-100 text-slate-800 text-[10px] uppercase font-bold border-b border-slate-200">
                                                    <th className="py-2 px-3 border-r border-slate-200">{t('mltLabTest.modal.tableHeaders.plateNo')}</th>
                                                    <th className="py-2 px-3 border-r border-slate-200">{t('mltLabTest.print.serialNo')}</th>
                                                    <th className="py-2 px-3 text-center border-r border-slate-200">{t('mltLabTest.print.cfuReading')}</th>
                                                    <th className="py-2 px-3">{t('mltLabTest.print.colonyMorphology')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200 text-[11px]">
                                                {selectedRecord.plateReadings.map((p) => (
                                                    <tr key={p.plateNo}>
                                                        <td className="py-2 px-3 font-semibold border-r border-slate-200">{t('mltLabTest.modal.plateLabel', { plateNo: p.plateNo })}</td>
                                                        <td className="py-2 px-3 font-mono border-r border-slate-200">{p.serialNo}</td>
                                                        <td className="py-2 px-3 text-center font-bold font-mono border-r border-slate-200">{p.cfu}</td>
                                                        <td className="py-2 px-3">{p.morphology || t('mltLabTest.modal.completed.defaultMorphology')}</td>
                                                    </tr>
                                                ))}
                                                <tr className="bg-slate-50 font-bold">
                                                    <td colSpan={2} className="py-2 px-3 border-r border-slate-200">{t('mltLabTest.print.averageCountPerPlate')}</td>
                                                    <td className="py-2 px-3 text-center font-mono border-r border-slate-200">{t('mltLabTest.print.averageCfu', { value: selectedRecord.averageCfuPerPlate })}</td>
                                                    <td className="py-2 px-3 text-emerald-700">{t('mltLabTest.print.calculatedBioburden', { value: selectedRecord.calculatedBioburden })}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-xs space-y-1.5">
                                        <div className="flex justify-between">
                                            <span className="font-bold text-slate-700">{t('mltLabTest.print.acceptanceLimit')}</span>
                                            <span className="font-bold text-slate-900">{selectedRecord.acceptanceLimit}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="font-bold text-slate-700">{t('mltLabTest.print.complianceStatement')}</span>
                                            <span className="font-bold text-emerald-700">{t('mltLabTest.print.compliesWithSpecification')}</span>
                                        </div>
                                        <div className="flex justify-between pt-1 border-t border-slate-200">
                                            <span className="font-bold text-slate-700">{t('mltLabTest.print.analystRemarks')}</span>
                                            <span className="text-slate-800">{selectedRecord.remarks}</span>
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-slate-200 flex justify-between items-center text-xs text-slate-600">
                                        <div>
                                            <p className="font-bold text-slate-800">{t('mltLabTest.print.testedBy', { name: selectedRecord.analystSignOff?.name || 'Dr. Sarah Jenkins' })}</p>
                                            <p className="text-[10px] text-slate-400">{t('mltLabTest.print.date', { date: selectedRecord.analystSignOff?.signedAt || '12-Oct-2026' })}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-slate-800">{t('mltLabTest.print.approvedBy', { name: selectedRecord.qcReviewSignOff?.name || 'Emily Watson' })}</p>
                                            <p className="text-[10px] text-slate-400">{t('mltLabTest.print.date', { date: selectedRecord.qcReviewSignOff?.reviewedAt || '12-Oct-2026' })}</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>
            </div>
            )}
        </div>
    );
};

export default MLTLabTest;